import * as React from 'react';
import {
	generatePath,
	useHistory,
	useRouteMatch,
} from 'react-router-dom';

import Tabs, { TabsProps } from 'antd/lib/tabs';

import { parseQuery } from '@common/typescript/utils/url';
import { replaceSearchUrl } from '@common/react/components/Utils/Utils';

export enum TabMode {
	Query = 0, // Store current tab as a query-param (?tab=key)
	Location = 1, // Store current tab as location-param (/path/tab)
}

interface BaseProps {
	mode?: TabMode;
	defaultKey: string;
	tabsUrlKey: string;

	searchParams?: Record<string, string>;
}

type ComponentProps = TabsProps & BaseProps;
type Props = React.PropsWithChildren<unknown> & ComponentProps;
interface Routing {
	history: ReturnType<typeof useHistory>;
	match: ReturnType<typeof useRouteMatch>;
}

function getTab(props: Pick<Props, 'defaultKey' | 'tabsUrlKey' | 'mode'>, routing: Routing, keyRef: string): string {
	switch (props.mode) {
	case TabMode.Location:
		return routing.match?.params[props.tabsUrlKey] || props.defaultKey;

	case TabMode.Query:
	default: {
		const query = parseQuery(routing.history.location.search);

		return query[props.tabsUrlKey] ?? keyRef;
	}
	}
}

type ChangeHandler = (key: string) => void;
interface FactoryProps extends Pick<Props, 'tabsUrlKey' | 'searchParams'> {
	history: ReturnType<typeof useHistory>;
	match: ReturnType<typeof useRouteMatch>;

	ref: React.MutableRefObject<string>;
	tab: string;
}

type ChangeHandlerFactory = (props: FactoryProps) => ChangeHandler;

const handlerFactory: Record<TabMode, ChangeHandlerFactory> = {
	[TabMode.Query]: (props: FactoryProps) => {
		return (key: string) => {
			const search = props.searchParams && props.searchParams[key]
				? `${props.history.location.search}&${props.searchParams[key]}`
				: '';
			props.ref.current = key;
			props.history.push({
				...props.history.location,
				pathname: props.history.location.pathname,
				search: props.tab === key
					? props.history.location.search
					: replaceSearchUrl(search, props.tabsUrlKey, key),
			});
		};
	},
	[TabMode.Location]: (props: FactoryProps) => {
		return (key: string) => {
			const search = props.searchParams && props.searchParams[key]
				? `${props.history.location.search}&${props.searchParams[key]}`
				: '';
			const params: Record<string, string> = {
				...props.match?.params as Record<string, string>,
				[props.tabsUrlKey]: key,
			};

			const path = generatePath(props.match?.path ?? '', params);
			const config: Partial<Location> = {
				hash: props.history.location.hash,
				pathname: path,
			};

			if (props.searchParams && props.searchParams[key] && props.tab !== key) {
				config.search = search;
			}

			if (props.tab === key) {
				config.search = props.history.location.search;
			}

			props.history.replace(config);
		};
	},
};

export const TabsWithUrl: React.FC<Props> = (props: Props) => {
	const {
		defaultKey,
		onChange,
		items,
		searchParams,
		tabsUrlKey,
		mode,
		...rest
	} = props;

	const history = useHistory();
	const match = useRouteMatch();

	const keyRef = React.useRef<string>(defaultKey);
	const [currentTab, setTab] = React.useState<string>(() => getTab(props, { history, match }, keyRef.current));

	const handler = React.useCallback<ChangeHandler>((key: string) => {
		const params: FactoryProps = {
			searchParams,
			history,
			tabsUrlKey,
			match,
			tab: currentTab,
			ref: keyRef,
		};
		const _handler = handlerFactory[mode ?? TabMode.Location];

		return _handler(params)(key);
	}, [mode, searchParams, history, tabsUrlKey, match, currentTab]);

	React.useEffect(() => {
		const mode = props.mode ?? TabMode.Location;
		const params: FactoryProps = {
			searchParams,
			history,
			tabsUrlKey,
			match,
			tab: currentTab,
			ref: keyRef,
		};
		const handler = handlerFactory[mode];

		const key = getTab(props, { history, match }, keyRef.current);
		handler(params)(key);
	}, []);

	React.useEffect(() => {
		const newActiveTab = getTab(props, { history, match }, keyRef.current);

		if (newActiveTab && newActiveTab !== currentTab) {
			setTab(newActiveTab);
			onChange?.(newActiveTab);
		}
	}, [history.location]);

	return (
		<Tabs
			{...rest}
			activeKey={currentTab}
			onChange={handler}
			items={items}
		/>
	);
};

TabsWithUrl.defaultProps = {
	mode: TabMode.Location,
};
